<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useScroll } from '@vueuse/core';
import { useGetOrganizationGroups } from '@ilteducation/customer-mgmt';
import { useLocale } from '../../composables/useLocale';
import type { GroupOutput } from '@ilteducation/customer-mgmt-types';
import { MdiFaIcon } from '@ilteducation/components';
import { faUserGroup, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { createDebounce } from '../../utils';
import GroupLabel from '../GroupLabel.vue';

const { t } = useLocale();

const props = defineProps<{
  organizationUid: string;
  selectedUid?: string;
  open: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', group: GroupOutput): void;
  (e: 'create-click', searchTerm: string): void;
}>();

const searchInputRef = ref<InstanceType<typeof HTMLInputElement> | null>(null);

// --- Search ---
const search = ref('');
const debouncedResetAndFetch = createDebounce(300);

// --- Groups list ---
const { loadGroups, loading } = useGetOrganizationGroups();
const groups = ref<GroupOutput[]>([]);
const currentPage = ref(1);
const totalItems = ref(0);
const PAGE_SIZE = 25;

const fetchGroups = async (page: number, replace: boolean) => {
  const result = await loadGroups(props.organizationUid, page, PAGE_SIZE, 'name', 'asc', search.value || undefined);
  const data = result?.data ?? [];
  groups.value = replace ? data : [...groups.value, ...data];
  totalItems.value = result?.meta?.totalItems ?? 0;
};

const resetAndFetch = () => {
  currentPage.value = 1;
  groups.value = [];
  totalItems.value = 0;
  fetchGroups(1, true);
};

watch(
  () => props.open,
  (open) => {
    if (open) {
      search.value = '';
      resetAndFetch();
      setTimeout(() => {
        searchInputRef.value?.focus();
      }, 50);
    }
  },
  { immediate: true },
);

const onEnter = () => {
  if (loading.value) return;
  if (groups.value.length === 1) {
    emit('select', groups.value[0]);
  } else if (search.value) {
    emit('create-click', search.value);
  }
};

const onSearchInput = () => debouncedResetAndFetch(resetAndFetch);

const listContainer = ref<HTMLElement | null>(null);
const { arrivedState } = useScroll(listContainer);

watch(
  () => arrivedState.bottom,
  async (isBottom) => {
    if (!isBottom || loading.value || groups.value.length >= totalItems.value) return;
    currentPage.value++;
    await fetchGroups(currentPage.value, false);
  },
);
</script>

<template>
  <v-card>
    <v-card-text class="pa-2">
      <v-text-field
        ref="searchInputRef"
        v-model="search"
        :placeholder="t('lcp.search')"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        @input="onSearchInput"
        @keydown.enter="onEnter"
      />
    </v-card-text>

    <v-divider />

    <v-list density="compact">
      <v-list-item :title="t('lcp.create_new_group')" @click="emit('create-click', search)">
        <template #prepend>
          <MdiFaIcon :icon="faPlus" size="small" class="mr-2" />
        </template>
      </v-list-item>
    </v-list>

    <v-divider />

    <div ref="listContainer" style="max-height: 240px; overflow-y: auto">
      <template v-if="loading && currentPage === 1">
        <v-list-item v-for="i in 3" :key="i">
          <v-skeleton-loader type="text" />
        </v-list-item>
      </template>

      <template v-else-if="groups.length === 0">
        <v-list-item :title="t('lcp.no_groups_found')" disabled />
      </template>

      <template v-else>
        <v-list-item
          v-for="group in groups"
          :key="group.uid"
          density="compact"
          :active="group.uid === selectedUid"
          active-color="primary"
          @click="emit('select', group)"
        >
          <template #title>
            <GroupLabel :name="group.name" :years-in-school="group.yearsInSchool" size="small" />
          </template>
          <template #prepend>
            <MdiFaIcon :icon="faUserGroup" size="small" class="mr-2" />
          </template>
          <template v-if="group.uid === selectedUid" #append>
            <v-icon color="primary" size="small">mdi-check</v-icon>
          </template>
        </v-list-item>
      </template>

      <v-progress-linear v-if="loading && currentPage > 1" indeterminate color="primary" height="2" />
    </div>
  </v-card>
</template>
